export enum BlockType {
  PATIENT = "patient",
  PROVIDER = "provider",
  LOCATION = "location",
  COVERAGE = "coverage",
  REFERRAL_INFORMATION = "referralInformation",
  PRIOR_AUTHORIZATION = "priorAuthorization",
  PROGRESS_NOTE = "progressNote",
  IMAGING_RESULTS = "imagingResults",
  LAB_RESULTS = "labResults",
  DOCUMENT_INFORMATION = "documentInformation",
  ADMIN = "admin",
  CONTINUITY_OF_CARE = "continuityOfCare",
  MEDICAL_HISTORY = "medicalHistory",
  MEDICAL_RECORD_REQUEST = "medicalRecordRequest",
  NOTIFICATION = "notification",
  PHARMACY_RELATED = "pharmacyRelated",
  EMERGENCY_SUMMARY = "emergencySummary",
  REFERRAL_RESPONSE = "referralResponse",
  CARE_PLAN = "carePlan",
  DME = "dme",
  CLEARANCE_REQUEST = "clearanceRequest",
  CONSENT = "consent",
  FINANCIAL = "financial",
  ID_CARD = "idCard",
  INSURANCE = "insurance",
  INTAKE = "intake",
  LEGAL = "legal",
  OPERATION_NOTE = "operationNote",
  PROCEDURE = "procedure",
}

export enum ProviderBlockKey {
  PRIMARY_CARE_PROVIDER = "primaryCareProvider",
  ORDERING_PROVIDER = "orderingProvider",
  REFERRING_PROVIDER = "referringProvider",
  RECEIVING_PROVIDER = "receivingProvider",
  PROVIDER = "provider",
}

// BlockKeys are used to identify and access specific blocks on EnrichedContent, and also determine the role.
export enum BlockKey {
  PATIENT = "patient",

  // Provider blocks - values sourced from ProviderBlockKey.
  PRIMARY_CARE_PROVIDER = ProviderBlockKey.PRIMARY_CARE_PROVIDER,
  ORDERING_PROVIDER = ProviderBlockKey.ORDERING_PROVIDER,
  REFERRING_PROVIDER = ProviderBlockKey.REFERRING_PROVIDER,
  RECEIVING_PROVIDER = ProviderBlockKey.RECEIVING_PROVIDER,
  PROVIDER = ProviderBlockKey.PROVIDER, // Generic provider

  // Location blocks
  RECEIVING_LOCATION = "receivingLocation",
  REFERRING_LOCATION = "referringLocation",

  // Coverage blocks
  PRIMARY_COVERAGE = "primaryCoverage",
  SECONDARY_COVERAGE = "secondaryCoverage",

  // Generic block
  DOCUMENT_INFORMATION = "documentInformation",

  // Classification-specific blocks
  REFERRAL_INFORMATION = "referralInformation",
  PRIOR_AUTHORIZATION = "priorAuthorization",
  PROGRESS_NOTE = "progressNote",
  IMAGING_RESULTS = "imagingResults",
  LAB_RESULTS = "labResults",
  ADMIN = "admin",
  CONTINUITY_OF_CARE = "continuityOfCare",
  MEDICAL_HISTORY = "medicalHistory",
  MEDICAL_RECORD_REQUEST = "medicalRecordRequest",
  NOTIFICATION = "notification",
  PHARMACY_RELATED = "pharmacyRelated",
  EMERGENCY_SUMMARY = "emergencySummary",
  REFERRAL_RESPONSE = "referralResponse",
  CARE_PLAN = "carePlan",
  DME = "dme",
  CLEARANCE_REQUEST = "clearanceRequest",
  CONSENT = "consent",
  FINANCIAL = "financial",
  ID_CARD = "idCard",
  INSURANCE = "insurance",
  INTAKE = "intake",
  LEGAL = "legal",
  OPERATION_NOTE = "operationNote",
  PROCEDURE = "procedure",
}

const BLOCK_KEYS: ReadonlySet<string> = new Set(Object.values(BlockKey));

export function isBlockKey(value: string): value is BlockKey {
  return BLOCK_KEYS.has(value);
}

/**
 * Blocks whose reviewed fields are written at the top level of section.reviewedContent
 */
export const BLOCKS_WITH_TOP_LEVEL_FIELDS: BlockKey[] = [
  BlockKey.PRIOR_AUTHORIZATION,
  BlockKey.REFERRAL_INFORMATION,
  BlockKey.DOCUMENT_INFORMATION,
  BlockKey.PROGRESS_NOTE,
  BlockKey.IMAGING_RESULTS,
  BlockKey.LAB_RESULTS,
  BlockKey.ADMIN,
  BlockKey.CONTINUITY_OF_CARE,
  BlockKey.MEDICAL_HISTORY,
  BlockKey.MEDICAL_RECORD_REQUEST,
  BlockKey.NOTIFICATION,
  BlockKey.PHARMACY_RELATED,
  BlockKey.EMERGENCY_SUMMARY,
  BlockKey.REFERRAL_RESPONSE,
  BlockKey.CARE_PLAN,
  BlockKey.DME,
  BlockKey.CLEARANCE_REQUEST,
  BlockKey.CONSENT,
  BlockKey.FINANCIAL,
  BlockKey.ID_CARD,
  BlockKey.INSURANCE,
  BlockKey.INTAKE,
  BlockKey.LEGAL,
  BlockKey.OPERATION_NOTE,
  BlockKey.PROCEDURE,
];

// Make this take classification
export function getBlockLabel(blockKey: BlockKey): string {
  switch (blockKey) {
    case BlockKey.PATIENT:
      return "Patient";
    case BlockKey.PRIMARY_CARE_PROVIDER:
      return "Primary Care Provider";
    case BlockKey.ORDERING_PROVIDER:
      return "Ordering Provider";
    case BlockKey.REFERRING_PROVIDER:
      return "Referring Provider";
    case BlockKey.RECEIVING_PROVIDER:
      return "Receiving Provider";
    case BlockKey.PROVIDER:
      return "Provider";
    case BlockKey.RECEIVING_LOCATION:
      return "Receiving Location";
    case BlockKey.REFERRING_LOCATION:
      return "Referring Location";
    case BlockKey.PRIMARY_COVERAGE:
      return "Primary Coverage";
    case BlockKey.SECONDARY_COVERAGE:
      return "Secondary Coverage";
    case BlockKey.REFERRAL_INFORMATION:
      return "Referral Information";
    case BlockKey.DOCUMENT_INFORMATION:
      return "Document Information";
    case BlockKey.PRIOR_AUTHORIZATION:
      return "Prior Authorization";
    case BlockKey.PROGRESS_NOTE:
      return "Progress Note";
    case BlockKey.IMAGING_RESULTS:
      return "Imaging Results";
    case BlockKey.LAB_RESULTS:
      return "Lab Results";
    case BlockKey.ADMIN:
      return "Admin";
    case BlockKey.CONTINUITY_OF_CARE:
      return "Continuity of Care";
    case BlockKey.MEDICAL_HISTORY:
      return "Medical History";
    case BlockKey.MEDICAL_RECORD_REQUEST:
      return "Medical Record Request";
    case BlockKey.NOTIFICATION:
      return "Notification";
    case BlockKey.PHARMACY_RELATED:
      return "Pharmacy Related";
    case BlockKey.EMERGENCY_SUMMARY:
      return "Emergency Summary";
    case BlockKey.REFERRAL_RESPONSE:
      return "Referral Response";
    case BlockKey.CARE_PLAN:
      return "Care Plan";
    case BlockKey.DME:
      return "DME";
    case BlockKey.CLEARANCE_REQUEST:
      return "Clearance Request";
    case BlockKey.CONSENT:
      return "Consent";
    case BlockKey.FINANCIAL:
      return "Financial";
    case BlockKey.ID_CARD:
      return "ID Card";
    case BlockKey.INSURANCE:
      return "Insurance";
    case BlockKey.INTAKE:
      return "Intake";
    case BlockKey.LEGAL:
      return "Legal";
    case BlockKey.OPERATION_NOTE:
      return "Operation Note";
    case BlockKey.PROCEDURE:
      return "Procedure";
    default:
      return String(blockKey);
  }
}

export function getBlockTypeFromKey(blockKey: BlockKey): BlockType {
  switch (blockKey) {
    case BlockKey.PATIENT:
      return BlockType.PATIENT;
    case BlockKey.PRIMARY_CARE_PROVIDER:
    case BlockKey.ORDERING_PROVIDER:
    case BlockKey.REFERRING_PROVIDER:
    case BlockKey.RECEIVING_PROVIDER:
    case BlockKey.PROVIDER:
      return BlockType.PROVIDER;
    case BlockKey.RECEIVING_LOCATION:
    case BlockKey.REFERRING_LOCATION:
      return BlockType.LOCATION;
    case BlockKey.PRIMARY_COVERAGE:
    case BlockKey.SECONDARY_COVERAGE:
      return BlockType.COVERAGE;
    case BlockKey.REFERRAL_INFORMATION:
      return BlockType.REFERRAL_INFORMATION;
    case BlockKey.DOCUMENT_INFORMATION:
      return BlockType.DOCUMENT_INFORMATION;
    case BlockKey.PRIOR_AUTHORIZATION:
      return BlockType.PRIOR_AUTHORIZATION;
    case BlockKey.PROGRESS_NOTE:
      return BlockType.PROGRESS_NOTE;
    case BlockKey.IMAGING_RESULTS:
      return BlockType.IMAGING_RESULTS;
    case BlockKey.LAB_RESULTS:
      return BlockType.LAB_RESULTS;
    case BlockKey.ADMIN:
      return BlockType.ADMIN;
    case BlockKey.CONTINUITY_OF_CARE:
      return BlockType.CONTINUITY_OF_CARE;
    case BlockKey.MEDICAL_HISTORY:
      return BlockType.MEDICAL_HISTORY;
    case BlockKey.MEDICAL_RECORD_REQUEST:
      return BlockType.MEDICAL_RECORD_REQUEST;
    case BlockKey.NOTIFICATION:
      return BlockType.NOTIFICATION;
    case BlockKey.PHARMACY_RELATED:
      return BlockType.PHARMACY_RELATED;
    case BlockKey.EMERGENCY_SUMMARY:
      return BlockType.EMERGENCY_SUMMARY;
    case BlockKey.REFERRAL_RESPONSE:
      return BlockType.REFERRAL_RESPONSE;
    case BlockKey.CARE_PLAN:
      return BlockType.CARE_PLAN;
    case BlockKey.DME:
      return BlockType.DME;
    case BlockKey.CLEARANCE_REQUEST:
      return BlockType.CLEARANCE_REQUEST;
    case BlockKey.CONSENT:
      return BlockType.CONSENT;
    case BlockKey.FINANCIAL:
      return BlockType.FINANCIAL;
    case BlockKey.ID_CARD:
      return BlockType.ID_CARD;
    case BlockKey.INSURANCE:
      return BlockType.INSURANCE;
    case BlockKey.INTAKE:
      return BlockType.INTAKE;
    case BlockKey.LEGAL:
      return BlockType.LEGAL;
    case BlockKey.OPERATION_NOTE:
      return BlockType.OPERATION_NOTE;
    case BlockKey.PROCEDURE:
      return BlockType.PROCEDURE;
  }
}
